import { useCallback } from 'react';
import { Box } from '@mui/material';
import { useStickyPosition } from 'src/hooks/useStickyPosition';
import { CampaignOrder } from '../CampaignOrder';
import { CampaignActions } from '../CampaignActions';
import { confirmAndPayStickyContainerSx } from '../../styles/createCampaign';
import { useLocation, useNavigate } from 'react-router';
import { useCampaignOrderData } from '../../hooks';
import { useFormContext, FieldErrors } from 'react-hook-form';
import { ConfirmAndPayFormData } from './index';
import {
  getFirstErrorField,
  scrollToErrorField
} from '../../utils/scrollToError';
import { useCitationUpdateRequest } from '../../hooks/useCitationUpdateRequest';
import { useCitation } from '../../Providers/CitationProvider';
import { convertFileToBase64 } from 'src/common/fileUtils';

export const ConfirmAndPayRightSide = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { campaignId, locationId } = locationState as { campaignId: string, locationId: string };
  const { mutateAsync: citationUpdateRequest, isPending } =
    useCitationUpdateRequest();
  const { handleSubmit, setFocus } = useFormContext<ConfirmAndPayFormData>();
  const { state: citationState, setError } = useCitation();

  const { containerRef, isSticky, stickyTop } = useStickyPosition({
    topOffset: 70
  });

  // Get order data without remove actions (read-only on confirm-and-pay page)
  const tableData = useCampaignOrderData({ enableRemoveActions: false });

  const onSubmit = async (data: ConfirmAndPayFormData) => {
    // Convert form data to FormData for API submission
    const formData = new FormData();

    // Business Location Info - send as JSON string
    formData.append(
      'businessLocationInfo',
      JSON.stringify({
        name: data.businessLocationInfo.name,
        country: data.businessLocationInfo.country,
        firstLine: data.businessLocationInfo.firstLine,
        secondLine: data.businessLocationInfo.secondLine,
        townCity: data.businessLocationInfo.townCity,
        stateCountyRegion: data.businessLocationInfo.stateCountyRegion,
        postcode: data.businessLocationInfo.postcode,
        businessPhoneNumber: data.businessLocationInfo.businessPhoneNumber,
        businessWebsiteUrl: data.businessLocationInfo.businessWebsiteUrl
      })
    );

    // Business Location Contact Info - send as JSON string
    const contactInfo: any = {
      contactFirstName: data.businessLocationContactInfo.contactFirstName,
      contactLastName: data.businessLocationContactInfo.contactLastName,
      contactEmail: data.businessLocationContactInfo.contactEmail,
      contactPhoneNumber: data.businessLocationContactInfo.contactPhoneNumber,
      faxNumber: data.businessLocationContactInfo.faxNumber
    };
    if (data.businessLocationContactInfo.openingDate instanceof Date) {
      contactInfo.openingDate =
        data.businessLocationContactInfo.openingDate.toISOString();
    }
    formData.append('businessLocationContactInfo', JSON.stringify(contactInfo));

    // About The Business - send as JSON string
    formData.append(
      'aboutTheBusiness',
      JSON.stringify({
        businessCategoryId: data.aboutTheBusiness.businessCategory?.id,
        numberOfEmployees: data.aboutTheBusiness.numberOfEmployees,
        businessDescription: data.aboutTheBusiness.businessDescription,
        openingHours: data.aboutTheBusiness.openingHours,
        paymentMethods: data.aboutTheBusiness.paymentMethods,
        socialMediaLinks: data.aboutTheBusiness.socialMediaLinks
      })
    );

    // Email Alerts
    formData.append('emailAlerts', data.emailAlerts || '');

    // Campaign Notes
    formData.append('campaignNotes', data.campaignNotes || '');

    // Terms Accepted
    formData.append('termsAccepted', data.termsAccepted.toString());

    // Business Images - append files if they exist (using 1-based indexing and dot notation to match cURL)
    // Business Images - append files if they exist (using 1-based indexing and dot notation to match cURL)
    await Promise.all(
      data.businessImages.map(async (image, index) => {
        if (image.file instanceof File) {
          try {
            const base64String = await convertFileToBase64(image.file);
            // Use 1-based indexing to match cURL format: businessImages[1].file
            const imageIndex = index + 1;
            formData.append(`businessImages[${imageIndex}].file`, base64String);
            formData.append(`businessImages[${imageIndex}].label`, image.label);
          } catch (error) {
            console.error('Error converting image to base64:', error);
          }
        }
      })
    );

    // Existing Citations - send as JSON string array
    formData.append(
      'existingCitations',
      JSON.stringify(citationState.selectedExistingCitations || [])
    );

    formData.append('totalExistingCitations', (citationState.campaignDetails?.existingCitations?.citations.length ?? 0).toString());

    // Available New Citations - send as JSON string array with citationSite, cost, and citationSiteType
    const availableNewCitationsPayload = (
      citationState.selectedAvailableNewCitations || []
    ).map((citation) => ({
      citationSite: citation.citationSite,
      cost: citation.cost,
      citationSiteType: citation.citationSiteType
    }));
    formData.append(
      'availableNewCitations',
      JSON.stringify(availableNewCitationsPayload)
    );

    // Duplicates are identified by the presence of existingCitations in campaignDetails
    const hasDuplicates =
      (citationState.campaignDetails?.existingCitations?.citations.length ??
        0) > 0;
    formData.append('removeDuplicates', hasDuplicates.toString());

    // Sync And Post - send as string from state
    formData.append('syncAndPost', citationState.syncAndPost.toString());

    // Add campaignId if available
    if (campaignId) {
      formData.append('campaignId', campaignId);
    }

    try {
      const response = await citationUpdateRequest({
        payload: formData,
        campaignId
      });
      if (response.status === 'success') {
        navigate('/local-seo/citations/campaign-in-progress', {
          state: { campaignId: campaignId }
        });
      }
    } catch (error) {
      setError({
          status: 'error',
          message: 'Failed to update citation. Please try again.'
        });
    }
  };

  const onError = useCallback(
    (validationErrors: FieldErrors<ConfirmAndPayFormData>) => {
      // When validation fails, scroll to the first error
      const firstErrorField = getFirstErrorField(validationErrors);
      if (firstErrorField) {
        setTimeout(() => {
          scrollToErrorField(firstErrorField, setFocus);
        }, 150);
      }
    },
    [setFocus]
  );

  const handleOneTimePayment = useCallback(() => {
    // Trigger form validation and submission
    // onError callback will be triggered if validation fails
    handleSubmit(onSubmit, onError)();
  }, [handleSubmit, onSubmit, onError]);

  const goToPreviousStep = useCallback(() => {
    navigate('/local-seo/citations/create-campaign', {
      state: { campaignId, locationId }
    });
  }, [navigate, campaignId, locationId]);

  return (
    <Box
      ref={containerRef}
      sx={{
        ...confirmAndPayStickyContainerSx,
        position: isSticky ? 'sticky' : 'relative',
        top: isSticky ? `${stickyTop}px` : 'auto'
      }}
    >
      <CampaignOrder
        data={tableData}
        actions={CampaignActions({
          onOneTimePayment: handleOneTimePayment,
          onSaveForLater: goToPreviousStep,
          primaryActionText: 'One-Time Payment',
          secondaryActionText: 'Previous Step',
          isLoading: isPending
        })}
      />
    </Box>
  );
};
